import React, { useContext, useEffect } from 'react';
import { merge } from 'lodash';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import GigCardListings from '@fiverr-private/gig_listings/src/entries/GigCardListings';
import { mixpanel } from '@fiverr-private/obs';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Image } from '@fiverr-private/media';
import ErrorBoundary from '../../errors/error_boundary/ErrorBoundary';
import { ListingsImpressionContext } from '../../../../../../shared/context/ListingsImpressionContainer/ListingsImpressionContainer';
import { reportHighlightRecommendationRendered } from '../../util/metric';
import { useAppContext } from '../../../context/listingsContext';
import type { BIContext, HighlightedRecommendationsProps } from './HighlightedRecommendations.types';
import { LOADING_SIZE, RECOMMENDATIONS_CONTEXT } from './HighlightedRecommendations.constants';

const getBIContext = (componentName: string, shouldSendRecommendations: boolean): BIContext => ({
    context: {
        sourceComponent: componentName,
        ...(shouldSendRecommendations && {
            queryParameters: {
                context: RECOMMENDATIONS_CONTEXT,
            },
        }),
    },
});

/**
 * Generic component to build highlighted recommendations display
 */
export const HighlightedRecommendations: React.FC<HighlightedRecommendationsProps> = ({
    className = '',
    title,
    description,
    listings,
    gigs = [],
    isLoading = false,
    componentName,
    iconPath,
    displayISpeak = false,
    shouldSendRecommendations = true,
}) => {
    const { onImpression } = useContext(ListingsImpressionContext);
    const { rollouts, activeFilters, clientExperimentsGroups } = useAppContext();
    const recommendationListings = merge({}, listings, getBIContext(componentName, shouldSendRecommendations));

    const [containerRef, containerId] = useImpressionContainer();

    const sendMixPanelEvent = () => {
        mixpanel.track('Viewed Highlighted Recommendations', { componentName });
    };

    const handleImpression = (impression) => {
        onImpression(impression);
        sendMixPanelEvent();
    };

    useEffect(() => reportHighlightRecommendationRendered(componentName), [componentName]);

    useImpressionItem({
        selfId: containerId,
        selfRef: containerRef,
        sendImpressionToBQ: false,
        enrichment: {
            ...(componentName && { name: componentName }),
            modalities_carousel: {},
        },
        onImpression: handleImpression,
    });

    return (
        <ErrorBoundary>
            <Container
                ref={containerRef}
                backgroundColor="yellow_100"
                borderRadius="md"
                padding="10"
                display={{ default: 'none', lg: 'block' }}
                className={className}
                width="100%"
            >
                <Stack alignItems="center" marginBottom="6" width="100%">
                    <Image src={iconPath} alt={componentName} width="54px" height="54px" />
                    <Container marginRight="4">
                        <Typography as="h4" color="grey_1200">
                            {title}
                        </Typography>
                        {description && (
                            <Typography as="p" color="grey_1100">
                                {description}
                            </Typography>
                        )}
                    </Container>
                </Stack>
                <GigCardListings
                    {...recommendationListings}
                    gigs={gigs}
                    isLoading={isLoading}
                    displayISpeak={displayISpeak}
                    size={isLoading ? LOADING_SIZE : gigs.length}
                    onImpression={onImpression}
                    isExperiential={true}
                    rollouts={rollouts}
                    gridContainerSize="md"
                    activeFilters={activeFilters}
                    clientExperimentsGroups={clientExperimentsGroups}
                />
            </Container>
        </ErrorBoundary>
    );
};
